import { useState } from "react";

import type { ReminderResponse } from "../../shared/api/client";
import { useT } from "../../shared/i18n";
import { Button, Chip, DateTimePicker, Field, Sheet } from "../../shared/ui";
import { localNow, quickPicks, type LocalDateTime } from "./time";

/** Move a reminder to a new wall-clock time in its own zone. */
export function RescheduleSheet({
  reminder,
  open,
  busy,
  onOpenChange,
  onSubmit,
  now = new Date(),
}: {
  reminder: ReminderResponse;
  open: boolean;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (localSchedule: LocalDateTime) => void;
  now?: Date;
}) {
  const t = useT();
  const current = localNow(reminder.timezone, now);
  const picks = quickPicks(current);
  const [value, setValue] = useState<LocalDateTime>(picks.tomorrow);
  // A repeat only needs a new anchor; a one-off must still lie ahead.
  const tooEarly = reminder.recurrence === "once" && value <= current;
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("reminders.rescheduleTitle")}
      footer={
        <Button
          className="w-full"
          disabled={busy || !value || tooEarly}
          loading={busy}
          onClick={() => {
            onSubmit(value);
          }}
        >
          {t("reminders.reschedule")}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Field
          label={t("reminders.newTime")}
          error={tooEarly ? t("reminders.pastTime") : undefined}
          hint={
            tooEarly
              ? undefined
              : `${t("reminders.timezone")} ${reminder.timezone}`
          }
        >
          {(props) => (
            <DateTimePicker {...props} value={value} onValueChange={setValue} />
          )}
        </Field>
        <div className="flex flex-wrap gap-2">
          {(["inHour", "evening", "tomorrow"] as const).map((pick) => (
            <Chip
              key={pick}
              selected={value === picks[pick]}
              onClick={() => {
                setValue(picks[pick]);
              }}
            >
              {t(`reminders.${pick}`)}
            </Chip>
          ))}
        </div>
      </div>
    </Sheet>
  );
}
